import { CashOfficeColumn } from "@/components/data-table/CashOfficeColumn";
import DataTable from "@/components/data-table/DataTable";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { DateConverter, ISODateFormat, STALE_TIME_INVOICE } from "@/constants";
import { UserContext } from "@/context/User/UserContext";
import { cn } from "@/lib/utils";
import {
	exportContributorInvoicesByDate,
	getAllContributorInvoicesByDate,
} from "@/services/external-contributors-functions";
import { format, subDays } from "date-fns";
import React, { useContext } from "react";
import { DateRange } from "react-day-picker";
import { IoCalendarSharp } from "react-icons/io5";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ContributorInvoices = () => {
	const user = useContext(UserContext);

	const [date, setDate] = React.useState<DateRange | undefined>({
		from: subDays(new Date(), 3),
		to: new Date(),
	});
	const [isPopUpOpen, setIsPopUpOpen] = React.useState(false);

	const invoices = useQuery({
		queryKey: ["invoice", "contributor", date?.from, date?.to],
		queryFn: () =>
			getAllContributorInvoicesByDate(
				user?.accessToken!,
				ISODateFormat(date?.from!),
				ISODateFormat(date?.to!)
			),
		enabled: !!user?.accessToken && !!date,
		staleTime: STALE_TIME_INVOICE,
	});

	const makeContributorExport = useMutation({
		mutationKey: ["export", "contributor"],
		mutationFn: () =>
			exportContributorInvoicesByDate(
				user?.accessToken!,
				ISODateFormat(date?.from!),
				ISODateFormat(date?.to!)
			),
		onSuccess: (data) => {
			makeContributorExport.reset();
			const url = window.URL.createObjectURL(new Blob([data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = `contributor_invoices_${DateConverter(
				date?.from!
			)}_to_${DateConverter(date?.to!)}.xlsx`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			toast.success("Success", {
				description: "Downloading Excel file",
			});
		},
		onError: () => {
			toast.error("Error", {
				description: "Error downloading Excel file",
			});
		},
	});

	const navigate = useNavigate();

	return (
		<div>
			<div className="flex justify-end py-2">
				<Popover open={isPopUpOpen} onOpenChange={setIsPopUpOpen}>
					<PopoverTrigger asChild>
						<Button
							id="date"
							variant={"outline"}
							className={cn(
								"w-[300px] justify-start text-left font-normal",
								!date && "text-muted-foreground"
							)}
						>
							<IoCalendarSharp className="mr-2 h-4 w-4" />
							{date?.from ? (
								date.to ? (
									<>
										{format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
									</>
								) : (
									format(date.from, "LLL dd, y")
								)
							) : (
								<span>Pick a date</span>
							)}
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-auto p-2" align="start">
						<Select
							onValueChange={(value) => {
								console.log(value);
								setDate({
									from: subDays(new Date(), parseInt(value)),
									to: new Date(),
								});
								// setIsPopUpOpen(false);
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select" />
							</SelectTrigger>
							<SelectContent position="popper">
								<SelectItem value="0">Today</SelectItem>
								<SelectItem value="1">Yesterday</SelectItem>
								<SelectItem value="3">Past 3 days</SelectItem>
								<SelectItem value="7">Past week</SelectItem>
							</SelectContent>
						</Select>
						<Calendar
							initialFocus
							mode="range"
							defaultMonth={date?.from}
							selected={date}
							onSelect={setDate}
							numberOfMonths={1}
						/>
						<Button
							className="w-full hover:bg-primary/30"
							onClick={() => setIsPopUpOpen(false)}
						>
							Confirm
						</Button>
					</PopoverContent>
				</Popover>
				<Button
					className="ml-2"
					variant="secondary"
					onClick={() => makeContributorExport.mutate()}
					disabled={makeContributorExport.isLoading}
				>
					{makeContributorExport.isLoading
						? "Processing..."
						: "Export Contributor Invoices"}
				</Button>
			</div>
			{invoices.data ? (
				<DataTable
					columns={CashOfficeColumn}
					data={invoices.data}
					rowClick={(id: number) => navigate(`/generalreport/${id}`)}
					rowId="invoicemasterid"
					searchColumn="raisedby"
				/>
			) : null}
			{invoices.isLoading ? (
				<div className="align-centre">
					<h5 className="text-center">Loading claims, please wait...</h5>
				</div>
			) : invoices.isError ? (
				<div className="align-centre">
					<h5 className="text-center">Error showing claims.</h5>
				</div>
			) : null}
		</div>
	);
};

export default ContributorInvoices;
